'use client';

import { c as _c } from "react/compiler-runtime";
import { useSelectedLayoutSegments } from 'next/navigation';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Group, Panel, Separator, useDefaultLayout } from 'react-resizable-panels';
import { IconRail, type IconRailGroup } from '@/components/shell/icon-rail';
import { InspectorSlot } from '@/components/shell/inspector-slot';
import { SubNavSlot } from '@/components/shell/sub-nav-slot';
import { useBreakpoint } from '@/components/shell/use-breakpoint';
export type ShellRootProps = {
  /** Page content. */
  children: ReactNode;
  /** Server-resolved @subnav slot. */
  subnav: ReactNode;
  /** Server-resolved @inspector slot. */
  inspector: ReactNode;
  /** Pre-rendered header (server component). */
  header: ReactNode;
  iconRailGroups: IconRailGroup[];
};
const RAIL_MIN_PX = 82;
const RAIL_LABEL_THRESHOLD_PX = 160;
const RAIL_MAX_PX = 280;
const SUBNAV_MIN_PX = 200;
const SUBNAV_MAX_PX = 480;
const INSPECTOR_MIN_PX = 240;
const INSPECTOR_MAX_PX = 560;
const SEPARATOR_CLASSNAME = "w-[0.2rem] bg-border transition-colors data-[separator='hover']:bg-foreground-highlight/50 data-[separator='active']:bg-foreground";
export const SHELL_ROOT_ID = 'shell-root';

// The placeholder segment Next.js pushes into `useSelectedLayoutSegments(slot)` when a
// parallel slot renders its `default.tsx`. Next's `getSelectedLayoutSegmentPath` strips
// only the trailing `__PAGE__` leaf, NOT this one, so it always rides along (see
// `next/dist/shared/lib/segment`). Mirrored locally rather than imported from Next internals.
const DEFAULT_SLOT_SEGMENT = '__DEFAULT__';

/**
 * Decides whether a parallel-route slot is genuinely showing a section, given the segments
 * `useSelectedLayoutSegments(slot)` returned.
 *
 * Our `@subnav`/`@inspector` slots are default-only: the root `default.tsx` (which renders
 * nothing) yields `['__DEFAULT__']`, while a matched section yields e.g.
 * `['settings', '__DEFAULT__']`. The slot is therefore active only when some segment is a real
 * section — i.e. not the `__DEFAULT__` placeholder. A bare `!!segments` test is wrong because
 * the array is always truthy (even `['__DEFAULT__']`), which would mount an empty panel on every
 * route.
 *
 * @param segments - The slot segments from `useSelectedLayoutSegments`, or `null` outside app router.
 * @returns `true` when the slot matched a real section, `false` for the default-only/empty case.
 */
export function isSlotActive(segments: string[] | null): boolean {
  return Array.isArray(segments) && segments.some(segment => segment !== DEFAULT_SLOT_SEGMENT);
}

/**
 * Resizable panel layout for the admin shell, adapting between mobile (single-column) and desktop (multi-panel) views.
 *
 * Manages panel widths via react-resizable-panels, detects parallel-route slot activity to show/hide
 * the subnav and inspector panels, and observes the rail width to toggle icon-only vs labeled mode.
 *
 * @param props.children - Main page content rendered in the center panel.
 * @param props.subnav - Server-resolved @subnav parallel-route slot.
 * @param props.inspector - Server-resolved @inspector parallel-route slot.
 * @param props.header - Pre-rendered header server component.
 * @param props.iconRailGroups - Grouped navigation sections passed through to the IconRail.
 */
export function ShellRoot(t0) {
  const $ = _c(30);
  const {
    children,
    subnav,
    inspector,
    header,
    iconRailGroups
  } = t0;
  const breakpoint = useBreakpoint();
  const subnavSegments = useSelectedLayoutSegments("subnav");
  const inspectorSegments = useSelectedLayoutSegments("inspector");
  let t1;
  if ($[0] !== subnavSegments) {
    t1 = isSlotActive(subnavSegments);
    $[0] = subnavSegments;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasSubnav = t1;
  let t2;
  if ($[2] !== inspectorSegments) {
    t2 = isSlotActive(inspectorSegments);
    $[2] = inspectorSegments;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasInspector = t2;
  const showInspector = hasInspector && (breakpoint === "wide" || breakpoint === "comfortable");
  let t3;
  if ($[4] !== hasSubnav) {
    t3 = hasSubnav ? ["subnav"] : [];
    $[4] = hasSubnav;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== showInspector) {
    t4 = showInspector ? ["inspector"] : [];
    $[6] = showInspector;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = ["rail", ...t3, "content", ...t4];
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const panelIds = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      getItem: _temp,
      setItem: _temp2
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== panelIds) {
    t7 = {
      id: SHELL_ROOT_ID,
      panelIds,
      storage: t6
    };
    $[12] = panelIds;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const {
    defaultLayout,
    onLayoutChanged
  } = useDefaultLayout(t7);
  const railElementRef = useRef(null);
  const [railWidthPx, setRailWidthPx] = useState(0);
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const el = railElementRef.current;
      if (!el || typeof ResizeObserver === "undefined") {
        return;
      }
      const ro = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) {
          setRailWidthPx(Math.round(entry.contentRect.width));
        }
      });
      ro.observe(el);
      return () => ro.disconnect();
    };
    t9 = [];
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] !== breakpoint || $[17] !== children || $[18] !== defaultLayout || $[19] !== hasSubnav || $[20] !== iconRailGroups || $[21] !== inspector || $[22] !== onLayoutChanged || $[23] !== railWidthPx || $[24] !== showInspector || $[25] !== subnav) {
    t10 = breakpoint === "mobile" || breakpoint === "tablet" ? <main className="relative min-w-0 overflow-hidden">{children}</main> : <Group id="shell" orientation="horizontal" defaultLayout={defaultLayout} onLayoutChanged={onLayoutChanged}><Panel id="rail" elementRef={railElementRef} minSize={RAIL_MIN_PX} maxSize={RAIL_MAX_PX} collapsible={true} collapsedSize={RAIL_MIN_PX} groupResizeBehavior="preserve-pixel-size"><IconRail groups={iconRailGroups} expanded={railWidthPx >= RAIL_LABEL_THRESHOLD_PX} /></Panel>{hasSubnav ? <><Separator className={SEPARATOR_CLASSNAME} /><Panel id="subnav" minSize={SUBNAV_MIN_PX} maxSize={SUBNAV_MAX_PX} collapsible={true} collapsedSize={0} groupResizeBehavior="preserve-pixel-size"><SubNavSlot>{subnav}</SubNavSlot></Panel></> : null}<Separator className={SEPARATOR_CLASSNAME} /><Panel id="content" minSize="70%" collapsible={false} groupResizeBehavior="preserve-relative-size"><main className="relative h-full min-w-0 overflow-hidden px-3">{children}</main></Panel>{showInspector ? <><Separator className={SEPARATOR_CLASSNAME} /><Panel id="inspector" minSize={INSPECTOR_MIN_PX} maxSize={INSPECTOR_MAX_PX} collapsible={true} collapsedSize={0} groupResizeBehavior="preserve-pixel-size"><InspectorSlot>{inspector}</InspectorSlot></Panel></> : null}</Group>;
    $[16] = breakpoint;
    $[17] = children;
    $[18] = defaultLayout;
    $[19] = hasSubnav;
    $[20] = iconRailGroups;
    $[21] = inspector;
    $[22] = onLayoutChanged;
    $[23] = railWidthPx;
    $[24] = showInspector;
    $[25] = subnav;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== header || $[28] !== t10) {
    t11 = <div className="grid h-svh grid-cols-[minmax(0,1fr)] grid-rows-[56px_1fr] overflow-hidden">{header}{t10}</div>;
    $[27] = header;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
function _temp2() {}
function _temp() {
  return null;
}